import { normalizeVirtualPath } from './virtualFolders';

export interface BuildCfosFileLinkInput {
  origin: string;
  pathname: string;
  /** Current location.search (with or without leading `?`). */
  search?: string;
  folder: string;
  fileId: string;
}

/**
 * Build an absolute CFOS deep link that opens Browse at `folder` and targets `fileId`.
 * Preserves existing query params (e.g. `tab=cfos`) and forces `view=browse`.
 */
export function buildCfosFileLink({
  origin,
  pathname,
  search = '',
  folder,
  fileId,
}: BuildCfosFileLinkInput): string {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  params.set('view', 'browse');
  const normalized = normalizeVirtualPath(folder);
  if (normalized === '/') {
    params.delete('folder');
  } else {
    params.set('folder', normalized);
  }
  params.set('file', fileId);
  const query = params.toString();
  const base = `${origin.replace(/\/$/, '')}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
  return query ? `${base}?${query}` : base;
}

/** Copy plain text to the clipboard with a legacy fallback. */
export async function copyTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to execCommand fallback (e.g. insecure context / permission denied).
    }
  }

  if (typeof document === 'undefined') {
    throw new Error('Clipboard is not available in this environment.');
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  textarea.remove();
  if (!ok) {
    throw new Error('Could not copy to clipboard.');
  }
}
